/**
 * Shared API error handling — single source of truth for network/timeout/4xx/5xx errors.
 * Use `apiFetch()` instead of raw `fetch()` in API clients so every call gets a timeout
 * and a consistent `ApiError` shape; use `getUserErrorMessage()` to turn any caught error
 * into safe, user-facing toast copy (never raw SQL/stack text).
 */

import { reportClientError } from './errorReporter';

export type ApiErrorKind = 'network' | 'timeout' | 'client' | 'server' | 'unknown';

export class ApiError extends Error {
  readonly status?: number;
  readonly kind: ApiErrorKind;
  /** Full parsed response body, when available — lets callers read backend
   * fields beyond `message` (e.g. `deactivation_reason`) without changing
   * every apiErrorFromResponse() call site's signature. */
  readonly data?: any;

  constructor(message: string, kind: ApiErrorKind, status?: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.data = data;
  }
}

const DEFAULT_TIMEOUT_MS = 20000;

/** Races a promise against a timeout so a hung backend can't spin the UI forever. */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new ApiError('The request took too long. Please check your connection and try again.', 'timeout'));
    }, timeoutMs);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}

/** fetch() with a request timeout — network failures are normalized to ApiError('network'). */
export async function apiFetch(url: string, options?: RequestInit, timeoutMs?: number): Promise<Response> {
  try {
    return await withTimeout(fetch(url, options), timeoutMs);
  } catch (err) {
    if (err instanceof ApiError) {
      // Timeouts are reported here (not withTimeout itself) so the failing
      // request URL is available.
      if (err.kind === 'timeout') {
        reportClientError({ type: 'network_error', message: `Timeout: ${url}`, severity: 'medium' });
      }
      throw err;
    }
    reportClientError({
      type: 'network_error',
      message: `Fetch failed: ${url} — ${err instanceof Error ? err.message : String(err)}`,
      severity: 'medium',
    });
    throw new ApiError('Unable to reach the server. Please check your internet connection.', 'network');
  }
}

/** Turn a non-ok Response's status (+ optional parsed body) into a typed, user-safe ApiError. */
export function apiErrorFromResponse(status: number, body?: { message?: string; error?: string } | null): ApiError {
  const backendMessage = body?.message || body?.error;
  if (status === 400) {
    return new ApiError(backendMessage || 'Invalid request. Please check the details and try again.', 'client', 400, body);
  }
  if (status === 401) {
    return new ApiError(backendMessage || 'Your session has expired. Please log in again.', 'client', 401, body);
  }
  if (status === 403) {
    return new ApiError(backendMessage || "You don't have permission to do this.", 'client', 403, body);
  }
  if (status === 404) {
    return new ApiError(backendMessage || 'The requested item was not found.', 'client', 404, body);
  }
  if (status >= 500) {
    // A 5xx from our own backend is always a real bug (client errors are 4xx) —
    // report it with whatever detail the backend sent, since the generic
    // user-facing message deliberately hides it.
    reportClientError({
      type: 'api_error',
      message: `HTTP ${status}: ${backendMessage || 'Server error'}`,
      severity: 'high',
    });
    return new ApiError('Something went wrong on our end. Please try again shortly.', 'server', status, body);
  }
  return new ApiError(backendMessage || `Request failed (HTTP ${status}).`, 'unknown', status, body);
}

/** Friendly, safe-to-show message for any thrown error — never leaks raw SQL/stack text. */
export function getUserErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    if (/sql|mysqli|stack trace|fatal error|undefined index|undefined variable/i.test(err.message)) {
      return 'Something went wrong on our end. Please try again shortly.';
    }
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}
